// Procurement-focused pricing + subscription ladder (aligned with IntakeModal
// procurement path and `procurementPricing.js`).

import {
  ADVISOR_TIER_FEATURES,
  PROCUREMENT_ENTERPRISE,
} from "./procurementPricing";

export const TierKey = {
  INDIVIDUAL: "individual",
  PORTFOLIO: "portfolio", 
  INSTITUTIONAL: "institutional",
  ENTERPRISE: "enterprise",
  /** Legacy aliases */
  RETAIL: "individual",
  PROFESSIONAL: "portfolio",
  STARTER: "individual",
  GROWTH: "professional",
  SCALE: "institutional",
};

export const GRANDFATHERED_SCALE_UNIT_CAP = 200;

export const Permission = {
  VENDOR_SCOUTING: "vendor_scouting",
  AI_NEGOTIATION: "ai_negotiation",
  ESCROW_PAYMENT: "escrow_payment",
  SAVINGS_TRACKING: "savings_tracking",
};

export const TIER_PERMISSIONS = {
  [TierKey.INDIVIDUAL]: [
    Permission.VENDOR_SCOUTING,
    Permission.AI_NEGOTIATION,
    Permission.SAVINGS_TRACKING,
  ],
  [TierKey.PORTFOLIO]: [
    Permission.VENDOR_SCOUTING,
    Permission.AI_NEGOTIATION,
    Permission.SAVINGS_TRACKING,
  ],
  [TierKey.INSTITUTIONAL]: [
    Permission.VENDOR_SCOUTING,
    Permission.AI_NEGOTIATION,
    Permission.SAVINGS_TRACKING,
  ],
  [TierKey.ENTERPRISE]: [
    Permission.VENDOR_SCOUTING,
    Permission.AI_NEGOTIATION,
    Permission.SAVINGS_TRACKING,
  ],
};

/**
 * Stripe checkout `selectedPlan` must match `backend/src/routes/misc/stripe.controller`
 * keys: `procurement_professional` | `procurement_institutional` (+ `-monthly` / `-annual` suffix inside getStripePriceId).
 */
export function getCheckoutPlanKeyForStripe(displayPlan) {
  const p = String(displayPlan || "").trim().toLowerCase();
  if (p === "professional" || p === "portfolio") {
    return "procurement_professional";
  }
  if (p === "institutional") return "procurement_institutional";
  if (p === "retail" || p === "individual") return "individual";
  return "individual";
}

/**
 * POST /api/subscriptions/change expects legacy {@link PlanKey} (`growth` | `scale`).
 * Procurement UI keys map to the Stripe-backed suite row used in `subscription.service`.
 */
export function subscriptionChangeApiPlanKey(displayOrSuiteKey) {
  const k = String(displayOrSuiteKey || "").trim().toLowerCase();
  if (k === "professional" || k === "portfolio") return "growth";
  if (k === "institutional") return "scale";
  return k;
}

/**
 * Canonical tier from Firestore / Stripe-facing fields.
 * Checks the same string sources users see after intake (subscription, selectedPlan, etc.).
 */
export function resolveUserTier(userInfo) {
  if (!userInfo) return TierKey.INDIVIDUAL;

  const nestedTier =
    userInfo.plan && typeof userInfo.plan === "object"
      ? userInfo.plan.tier || userInfo.plan.key
      : null;

  const raw =
    userInfo.subscription ??
    userInfo.subscriptionPlan ??
    userInfo.planKey ??
    userInfo.selectedPlan ??
    nestedTier ??
    (typeof userInfo.plan === "string" ? userInfo.plan : null);

  const planLower = String(raw ?? "").trim().toLowerCase();
  if (!planLower) return TierKey.INDIVIDUAL;

  if (planLower.includes("enterprise")) return TierKey.ENTERPRISE;
  if (planLower.includes("institutional")) return TierKey.INSTITUTIONAL;
  if (planLower.includes("professional") || planLower.includes("portfolio")) return TierKey.PORTFOLIO;
  if (planLower.includes("retail") || planLower.includes("individual")) return TierKey.INDIVIDUAL;

  // Legacy SaaS keys → closest procurement tier
  if (planLower === "free" || planLower === "starter") return TierKey.INDIVIDUAL;
  if (
    planLower === "pro" ||
    planLower === "growth" ||
    planLower === "business"
  ) {
    return TierKey.PORTFOLIO;
  }
  if (planLower === "scale") return TierKey.INSTITUTIONAL;

  return TierKey.INDIVIDUAL;
}

export function userHasPermission(userInfo, permission) {
  const tier = resolveUserTier(userInfo);
  const tierPermissions = TIER_PERMISSIONS[tier] || [];
  return tierPermissions.includes(permission);
}

/** Used by `featureGate.js` (tier string may be legacy alias). */
export function tierHasPermission(tier, permission) {
  const list = TIER_PERMISSIONS[tier] || [];
  return list.includes(permission);
}

/** Numeric config used by helpers and legacy imports */
export const PROCUREMENT_PRICING = {
  [TierKey.INDIVIDUAL]: {
    monthlyFee: 0,
    jobFee: 25,
    savingsShare: 0,
    concurrentJobs: 1,
    name: "Individual",
    unitCap: 0,
  },
  [TierKey.PORTFOLIO]: {
    monthlyFee: 149,
    jobFee: 12, // Legacy tier fee field (Portfolio)
    savingsShare: 0,
    concurrentJobs: 5,
    name: "Portfolio",
    unitCap: 20,
  },
  [TierKey.INSTITUTIONAL]: {
    monthlyFee: 499,
    jobFee: 20,
    savingsShare: 0,
    concurrentJobs: 25,
    name: "Institutional",
    unitCap: 25,
  },
  [TierKey.ENTERPRISE]: {
    monthlyFee: null,
    jobFee: null,
    savingsShare: null,
    concurrentJobs: null,
    name: "Enterprise",
    unitCap: null,
  },
};

export function getPricingForTier(tier) {
  return PROCUREMENT_PRICING[tier] || PROCUREMENT_PRICING[TierKey.INDIVIDUAL];
}

export function getRawSubscriptionPlanKey(userInfo) {
  return resolveUserTier(userInfo);
}

export function hasVendorAccess() {
  return true;
}

export function hasAIAccess() {
  return true;
}

export function hasEscrowAccess() {
  return false; // Escrow functionality removed
}

function buildDisplayPricingLadder() {
  const d = ADVISOR_TIER_FEATURES;
  return {
    [TierKey.INDIVIDUAL]: {
      name: d.individual?.name || "Individual",
      tagline: d.individual?.tagline || "Great for homeowners. No monthly fees.",
      monthlyUSD: 0,
      priceLabel: "$0/month",
      unitCap: "Pay per search",
      type: "public",
      featured: true,
    },
    [TierKey.PORTFOLIO]: {
      name: d.portfolio?.name || "Portfolio",
      tagline: d.portfolio?.tagline || "Best for 5-20 units.",
      monthlyUSD: 149,
      priceLabel: "$149/month",
      unitCap: "No longer available",
      type: "public",
      featured: false,
    },
    [TierKey.INSTITUTIONAL]: {
      name: d.institutional?.name || "Institutional", 
      tagline: d.institutional?.tagline || "For large property managers.",
      monthlyUSD: 499,
      priceLabel: "$499/month",
      unitCap: "No longer available",
      type: "public",
      featured: false,
    },
    [TierKey.ENTERPRISE]: {
      name: "Legacy Plan",
      tagline: "No longer available",
      monthlyUSD: null,
      type: "disabled",
      unitCap: "Use Institutional plan instead", 
      featured: false,
    },
  };
}

const DISPLAY_LADDER = buildDisplayPricingLadder();

/** Numeric caps + prices for upgrade / feature-gate helpers */
export const PRICING_2026 = (() => {
  const ladder = buildDisplayPricingLadder();
  const out = {};
  for (const k of Object.keys(PROCUREMENT_PRICING)) {
    const base = PROCUREMENT_PRICING[k];
    const row = ladder[k] || {};
    const cap =
      k === TierKey.ENTERPRISE
        ? 250
        : typeof base.concurrentJobs === "number"
          ? base.concurrentJobs
          : typeof base.unitCap === "number"
            ? base.unitCap
            : 0;
    out[k] = {
      ...row,
      name: base.name,
      monthlyUSD:
        row.monthlyUSD != null ? row.monthlyUSD : base.monthlyFee ?? 0,
      unitCap: cap,
    };
  }
  return out;
})();

/** Retail = pay-per-job; no recurring subscription to cancel in Stripe sense */
export function isFreeTierUser(userInfo) {
  return resolveUserTier(userInfo) === TierKey.INDIVIDUAL;
}

export function normalizePlanKeyForSchedule(planKey, _pricingTierGroup) {
  const k = String(planKey || "").trim().toLowerCase();
  if (
    k === TierKey.INDIVIDUAL ||
    k === TierKey.PORTFOLIO ||
    k === TierKey.INSTITUTIONAL ||
    k === TierKey.ENTERPRISE
  ) {
    return k;
  }
  if (k === "free" || k === "starter") return TierKey.INDIVIDUAL;
  if (k === "pro" || k === "growth" || k === "business") {
    return TierKey.PORTFOLIO;
  }
  if (k === "scale") return TierKey.INSTITUTIONAL;
  return TierKey.INDIVIDUAL;
}

export function getPricingSchedule(_pricingTierGroup) {
  return { ...DISPLAY_LADDER };
}

export function getPricingScheduleWithFree(pricingTierGroup) {
  return getPricingSchedule(pricingTierGroup);
}

export function getDisplayPricingLadder(pricingTierGroup) {
  return getPricingSchedule(pricingTierGroup);
}

export function getMarket2026DisplaySchedule() {
  return { ...DISPLAY_LADDER };
}

export const getHomePricingGridPlanOrder = (_group) => [
  TierKey.INDIVIDUAL,
  TierKey.PORTFOLIO,
  TierKey.INSTITUTIONAL,
  TierKey.ENTERPRISE,
];

export function getRecommendedTierForUnits(unitCount) {
  const tiers = Object.entries(PRICING_2026)
    .filter(([key]) => key !== TierKey.ENTERPRISE)
    .sort(([, a], [, b]) => (a.unitCap || 0) - (b.unitCap || 0));
  const n = Number(unitCount) || 0;
  for (const [key, plan] of tiers) {
    if (n <= (plan.unitCap || 0)) return key;
  }
  return TierKey.ENTERPRISE;
}

/** @param {number} [units] - when provided, door-style tier pick for upgrade UI */
export function getRecommendedTier(units, _group, _date) {
  if (typeof units === "number" && !Number.isNaN(units)) {
    return getRecommendedTierForUnits(units);
  }
  return TierKey.PROFESSIONAL;
}

export function formatMonthlyUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "0";
  return Number(amount).toLocaleString("en-US");
}

export function formatAnnualDisplayUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "0";
  return Number(amount).toLocaleString("en-US");
}

/** Procurement ladder is monthly-only in product today */
export function getAnnualDisplayUsd(_planKey, _pricingTierGroup) {
  return null;
}

export function canAddUnit() {
  return true;
}

export function getEffectiveUnitCap() {
  return Infinity;
}

export function getNextTierUpgradeName() {
  return "Professional";
}

export const FREE_TIER = {
  planKey: TierKey.INDIVIDUAL,
  ctaLabel: "Use Retail",
  ctaSubline: "$0/mo · pay per job",
};

export const ENTERPRISE_TIER = {
  planKey: TierKey.ENTERPRISE,
  ctaLabel: "CONTACT US",
  unitCap: "Legacy plan - no longer available",
  features: ["Contact support for migration to free account"],
};

export const PRICING = PROCUREMENT_PRICING;

export const TIER_LABOR_HOURS_SAVED_2026 = {
  [TierKey.INDIVIDUAL]: 10,
  [TierKey.PORTFOLIO]: 25,
  [TierKey.INSTITUTIONAL]: 50,
  [TierKey.ENTERPRISE]: 100,
  /** Legacy column keys (marketing / feature matrix) */
  free: 10,
  starter: 10,
  pro: 25,
  growth: 25,
  scale: 50,
  business: 50,
};

export const TIER_VALUE_BADGES_2026 = {
  [TierKey.PORTFOLIO]: "Recommended",
  [TierKey.INSTITUTIONAL]: "5% verified savings",
  [TierKey.ENTERPRISE]: "Custom economics",
};

export const HOME_PRICING_PLAN_COLUMN_COPY = {
  [TierKey.INDIVIDUAL]: { headline: ADVISOR_TIER_FEATURES.individual?.name || "Individual" },
  [TierKey.PORTFOLIO]: { headline: ADVISOR_TIER_FEATURES.portfolio?.name || "Portfolio" },
  [TierKey.INSTITUTIONAL]: { headline: ADVISOR_TIER_FEATURES.institutional?.name || "Institutional" },
  [TierKey.ENTERPRISE]: { headline: "Legacy Plan" },
};

export const SHARED_PLAN_FEATURES = [
  "Help figuring out what is wrong",
  "Fair cost ranges before you hire",
  "Vetted local pros for your job",
  "Clear bids you can compare",
];

export const ROI_SUBHEADER_COPY =
  "Know more before you pay a contractor";
export const COST_OF_VACANCY_TOOLTIP =
  "Average cost per day of vacant unit";

export const FREE_TIER_LEGACY = TierKey.INDIVIDUAL;
export const ENTERPRISE_TIER_LEGACY = TierKey.ENTERPRISE;

export function openEnterpriseSalesWindow() {
  const href =
    (typeof process !== "undefined" &&
      process.env.REACT_APP_ENTERPRISE_CTA_HREF) ||
    "mailto:sales@clumse.app?subject=Enterprise%20Inquiry";
  window.open(href, "_blank", "noopener,noreferrer");
}
